import React from 'react';
import { FileText, AlertTriangle, Calendar, Clock, CreditCard, File, Landmark } from 'lucide-react';

/**
 * Компонент для отображения требований по выбранной визе
 */
const VisaRequirementsSidebar = ({
  selectedCountry,
  selectedVisaType,
  countries,
  visaTypes
}) => {
  // Получаем данные о выбранной стране и типе визы
  const country = countries.find(c => c.id === selectedCountry) || {};
  const visaType = visaTypes.find(t => t.id === selectedVisaType) || {};

  // Сроки обработки визы в зависимости от страны
  const processingTimes = {
    schengen: '7-14 рабочих дней',
    usa: '1-2 месяца',
    uk: '3-5 недель',
    canada: '4-6 недель',
    australia: '2-4 недели',
    china: '5-7 рабочих дней',
    japan: '5-10 рабочих дней',
    turkey: '3-5 рабочих дней',
    egypt: '2-5 рабочих дней',
    thailand: '3-5 рабочих дней',
    default: '10-15 рабочих дней'
  };

  // Требуемые документы в зависимости от типа визы
  const documentRequirements = {
    tourist: [
      'Загранпаспорт (срок действия не менее 6 месяцев после окончания поездки)',
      'Анкета (заполненная и подписанная)',
      'Цветные фотографии (3.5x4.5 см, на белом фоне)',
      'Подтверждение проживания (бронь отеля)',
      'Билеты или бронь билетов туда и обратно',
      'Медицинская страховка',
      'Выписка с банковского счета',
      'Справка с места работы с указанием должности и оклада'
    ],
    business: [
      'Загранпаспорт (срок действия не менее 6 месяцев после окончания поездки)',
      'Анкета (заполненная и подписанная)',
      'Цветные фотографии (3.5x4.5 см, на белом фоне)',
      'Приглашение от принимающей компании',
      'Сопроводительное письмо от работодателя',
      'Подтверждение проживания',
      'Медицинская страховка',
      'Выписка с банковского счета или справка о доходах'
    ],
    work: [
      'Загранпаспорт (срок действия не менее 1 года)',
      'Анкета (заполненная и подписанная)',
      'Цветные фотографии (3.5x4.5 см, на белом фоне)',
      'Разрешение на работу или приглашение от работодателя',
      'Трудовой договор или оферта',
      'Документы об образовании и квалификации',
      'Медицинская страховка',
      'Медицинские справки (для некоторых стран)',
      'Справка об отсутствии судимости'
    ],
    student: [
      'Загранпаспорт (срок действия не менее 1 года)',
      'Анкета (заполненная и подписанная)',
      'Цветные фотографии (3.5x4.5 см, на белом фоне)',
      'Приглашение от учебного заведения или подтверждение о зачислении',
      'Документы об образовании',
      'Подтверждение оплаты обучения',
      'Подтверждение проживания',
      'Медицинская страховка',
      'Выписка с банковского счета'
    ],
    default: [
      'Загранпаспорт',
      'Анкета',
      'Цветные фотографии',
      'Подтверждение цели поездки',
      'Подтверждение финансовых гарантий',
      'Медицинская страховка'
    ]
  };

  // Особые требования для конкретных стран
  const specialRequirements = {
    usa: [
      'Собеседование в консульстве обязательно',
      'Заполнение электронной анкеты DS-160',
      'ESTA не подходит для длительного пребывания'
    ],
    uk: [
      'Сбор биометрических данных',
      'Подтверждение знания английского языка (для некоторых типов виз)',
      'Подтверждение места проживания в Великобритании'
    ],
    schengen: [
      'Подтверждение платежеспособности (наличие 70-90 евро на день пребывания)',
      'Медицинская страховка с покрытием не менее 30 000 евро',
      'Маршрут путешествия (для туристической визы)'
    ],
    china: [
      'Подтверждение бронирования всех отелей на весь период поездки',
      'Оригиналы и копии всех документов',
      'Действующая медицинская страховка'
    ],
    japan: [
      'Детальный план поездки',
      'Подтверждение финансовой состоятельности',
      'Информация о местах проживания на каждый день'
    ]
  };

  // Получаем сроки обработки для выбранной страны
  const processingTime = processingTimes[selectedCountry] || processingTimes.default;
  
  // Получаем перечень документов для выбранного типа визы
  const documents = documentRequirements[selectedVisaType] || documentRequirements.default;
  
  // Получаем особые требования для выбранной страны
  const specialReqs = specialRequirements[selectedCountry] || [];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <FileText className="w-5 h-5 mr-2 text-purple-500" />
        Требования для получения визы
      </h3>

      <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg mb-4">
        <p className="text-sm text-purple-800 dark:text-purple-300 font-medium">
          {country.name || 'Выбранная страна'}, {visaType.name || 'выбранный тип визы'}
        </p>
      </div>

      {/* Срок обработки визы */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
          <Clock className="w-4 h-4 mr-1 text-blue-500" />
          Срок рассмотрения
        </h4>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {processingTime}
        </p>
      </div>

      {/* Консульский сбор */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
          <CreditCard className="w-4 h-4 mr-1 text-emerald-500" />
          Стандартный консульский сбор
        </h4>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {country.defaultFee} € (или эквивалент в другой валюте)
        </p>
      </div>

      {/* Требуемые документы */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
          <File className="w-4 h-4 mr-1 text-amber-500" />
          Необходимые документы
        </h4>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
          {documents.map((doc, index) => (
            <li key={index} className="flex items-start">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-purple-500 mt-1.5 mr-2"></span>
              <span>{doc}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Особые требования */}
      {specialReqs.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
            <AlertTriangle className="w-4 h-4 mr-1 text-red-500" />
            Особые требования
          </h4>
          <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
            {specialReqs.map((req, index) => (
              <li key={index} className="flex items-start">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 mr-2"></span>
                <span>{req}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Адрес посольства или консульства */}
      <div className="mt-5 bg-gray-50 dark:bg-gray-700/30 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
          <Landmark className="w-4 h-4 mr-1 text-indigo-500" />
          Где подавать документы
        </h4>
        <p className="text-xs text-gray-600 dark:text-gray-400">
          {getSubmissionPlaceInfo(selectedCountry)}
        </p>
      </div>

      <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
        Информация актуальна на {new Date().toLocaleDateString('ru-RU')}. Требования консульств могут меняться, рекомендуем уточнять актуальность на официальных сайтах.
      </div>
    </div>
  );
};

// Функция возвращает информацию о месте подачи документов в зависимости от страны
const getSubmissionPlaceInfo = (countryId) => {
  const submissions = {
    schengen: 'Визовые центры VFS Global или BLS International (в зависимости от страны Шенгена) в Москве, Санкт-Петербурге и других городах России. Некоторые страны принимают документы напрямую в консульствах.',
    usa: 'Документы подаются в Визовые центры США в Москве. Личное собеседование в консульских отделах посольства США.',
    uk: 'Визовые центры VFS Global в крупных городах России. Необходимо предварительное заполнение онлайн-анкеты.',
    canada: 'Визовый центр Канады в Москве. Для подачи необходимо предварительно создать аккаунт на сайте IRCC.',
    china: 'Консульский отдел Посольства КНР в Москве или генеральные консульства в Санкт-Петербурге, Екатеринбурге, Иркутске, Хабаровске, Владивостоке, Казани.',
    japan: 'Консульский отдел Посольства Японии в Москве или генеральные консульства в Санкт-Петербурге, Хабаровске, Владивостоке.',
    turkey: 'Визовый центр Турции или подача онлайн через систему e-Visa.',
    thailand: 'Консульский отдел Посольства Таиланда в Москве или генеральные консульства в Санкт-Петербурге, Екатеринбурге, Казани, Новосибирске, Владивостоке.',
    default: 'Посольство или консульство страны в Москве, визовые центры в крупных городах России. Для уточнения места подачи документов рекомендуем посетить официальный сайт представительства.',
  };

  return submissions[countryId] || submissions.default;
};

export default VisaRequirementsSidebar;